import React, { FC, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Checkbox from "shared/Checkbox/Checkbox";

interface KitFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    selectedType: string | undefined; // Make this generic? Renaming prop might be better but for minimal changes "selectedType" acts as "selectedValue"
    onApply: (type: string | undefined) => void;
    filterOptions: { id: string; label: string; value: string | undefined }[];
}

const KitFilterModal: FC<KitFilterModalProps> = ({
    isOpen,
    onClose,
    title,
    selectedType,
    onApply,
    filterOptions,
}) => {
    const [tempSelectedType, setTempSelectedType] = useState<string | undefined>(selectedType);

    useEffect(() => {
        if (isOpen) {
            setTempSelectedType(selectedType);
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen, selectedType]);

    const handleCheckboxChange = (value: string | undefined, checked: boolean) => {
        if (checked) {
            setTempSelectedType(value);
        } else {
            // If unchecking the currently selected one, we effectively select "undefined" (All) 
            // providing we don't enforce at least one selection. 
            // If we want radio-button behavior where clicking another selects it, updates state.
            // If clicking the same one unchecks it -> verify logic.
            if (tempSelectedType === value) {
                setTempSelectedType(undefined);
            }
        }
    };

    const handleApply = () => {
        onApply(tempSelectedType);
        onClose();
    };

    const handleCancel = () => {
        setTempSelectedType(selectedType);
        onClose();
    };

    if (!isOpen) return null;

    const modalContent = (
        <div
            className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-end justify-center"
            onClick={handleCancel}
        >
            <div
                className="w-full max-w-md max-h-[85vh] rounded-t-2xl bg-white shadow-xl flex flex-col border border-gray-200 overflow-hidden animate-slide-up"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center border-b px-6 py-4 bg-white">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {title}
                    </h2>
                    <button
                        onClick={handleCancel}
                        className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="px-6 py-4 overflow-y-auto flex-1">
                    <div className="space-y-3">
                        <p className="text-sm text-gray-500 mb-4">
                            Choose your preferred {title.toLowerCase()}
                        </p>
                        <div className="space-y-3">
                            {filterOptions.filter(f => f.id !== 'all').map((option) => (
                                <div key={option.id} className="flex items-center gap-3">
                                    <Checkbox
                                        checked={tempSelectedType === option.value}
                                        name={option.label}
                                        label={option.label}
                                        onChange={(checked) => handleCheckboxChange(option.value, checked)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="px-6 py-4 border-t bg-white">
                    <div className="flex gap-3">
                        <ButtonSecondary
                            variant="cancel"
                            onClick={handleCancel}
                            className="flex-1 border border-gray-300 py-3 rounded-xl text-sm font-medium"
                        >
                            Cancel
                        </ButtonSecondary>

                        <ButtonPrimary
                            onClick={handleApply}
                            className="flex-1 py-3 rounded-xl text-sm font-medium transition-all"
                        >
                            Apply Filters
                        </ButtonPrimary>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default KitFilterModal;
